import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Chip, FormControl, Grid, IconButton, Input, InputAdornment, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { makeStyles } from "@mui/styles";
import { Add } from "@mui/icons-material";
import { debounce, get, identity, map, pickBy } from "lodash";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import moment from "moment";
import { ERRORS_MESSAGE } from "../../constants";
import { userSelector } from "../../store/slices/userSlice";
import DataGridTable from "../dataGridTable/DataGridTable";
import { announcementPostSelector, getAnnouncementPosts, saveAnnouncementPostValue, startAnnouncementPost } from "../../store/slices/announcementPostSlice";


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  margin: {
    margin: theme.spacing(1),
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  textField: {
    width: "61ch",
  },
}));


const AnnouncementPostList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [searchTerm, setSearchTerm] = React.useState("");
  const user = useSelector(userSelector);
  const userId = user?._id;
  const { announcementPosts, isGettingAnnouncementPosts, isAnnouncementPostStarting, page } = useSelector(announcementPostSelector);
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [params, setParams] = useState({ offset: 1 });

  const masterViewColumns = [
    {
      field: "title",
      headerName: "Title",
      type: "string",
      width: 400,
      editable: false,
    },
    {
      field: "status",
      headerName: "Status",
      type: "string",
      width: 150,
      editable: false,
      renderCell: (params) => (
        <Chip label={params.value} color={params.value === "active" ? "success" : "error"} variant="outlined" size="small" />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      type: "string",
      width: 120,
      editable: false,
      renderCell: (params) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value.date}</Typography>
          {/* <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.time}</Typography> */}
        </div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      type: "string",
      width: 120,
      editable: false,
      renderCell: (params) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value.date}</Typography>
          {/* <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.time}</Typography> */}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      editable: false,
      renderCell: (params) => (
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handle.editAnnouncementPost(params?.id);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const handle = {
    getAllAnnouncementPostsFn: (cursor, search) => {
      if (userId) {
        dispatch(getAnnouncementPosts({ userId, cursor, search }));
      }
    },
    handleAnnouncementPostSearch: (searchTerm) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) &&
        handle.getAllAnnouncementPostsFn(params?.offset, searchTerm);
    },
    startAnnouncementPost: () => {
      dispatch(startAnnouncementPost({})).then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, {
            variant: "error",
            autoHideDuration: 5000,
          });

        } else {
          const data = res?.payload?.data?.data;
          navigate(`/post/${data?._id}`);
        }
      })
    },
    editAnnouncementPost: (announcementId) => {
      navigate(`/post/${announcementId}`);
    }
  };

  useEffect(() => {
    if (userId && params?.offset) {
      handle.getAllAnnouncementPostsFn(params?.offset, searchTerm);
      dispatch(saveAnnouncementPostValue({ page: params?.offset }));
    }
  }, [params?.offset, userId]);

  // Some API clients return undefined while loading
  // Following lines are here to prevent `rowCountState` from being undefined during the loading
  const [rowCountState, setRowCountState] = useState(announcementPosts?.totalDocs || 0);

  useEffect(() => {
    setRowCountState((prevRowCountState) => announcementPosts?.totalDocs ?? prevRowCountState);
  }, [announcementPosts?.totalDocs]);

  useEffect(() => {
    const transformAnnouncement = (doc) => {

      const createdAt = new Date(get(doc, "createdAt"));
      const updatedAt = new Date(get(doc, "updatedAt"));

      return pickBy(
        {
          id: get(doc, "_id"),
          title: get(doc, "title"),
          status: get(doc, "status"),
          createdAt: {
            date: moment(createdAt).format("D-M-YY"),
            // time: moment(createdAt).format("h:mm a"),
          },
          updatedAt: {
            date: moment(updatedAt).format("D-M-YY"),
            // time: moment(updatedAt).format("h:mm a"),
          },
        },
        identity,
      )
    }


    const transformedRows = map(get(announcementPosts, "docs"), transformAnnouncement);
    setRows(transformedRows);
  }, [announcementPosts]);

  const debounced = useCallback(debounce(handle.handleAnnouncementPostSearch, 500), []);

  return (
    <React.Fragment>
      <Grid container item direction="column" spacing={2} style={{ padding: "20px", minWidth: "99vw" }}>

        <Grid
          item
          style={{
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
            display: "flex",
            alignItems: "center",
            columnGap: "40px",
          }}
        >
          <Typography
            variant="h1"
            style={{
              fontSize: "24px",
              fontWeight: 800,
              letterSpacing: "-0.5px",
            }}
          >
            Posts
          </Typography>
          <div className={classes.root}>
            <FormControl className={clsx(classes.margin, classes.textField)}>
              <Input
                className="input-border-bottom"
                id="application-search"
                autoComplete={false}
                variant="filled"
                placeholder="Search…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  debounced(e.target.value, 1000);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                endAdornment={
                  searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSearchTerm("");
                          searchTerm !== "" && debounced("", 1000);
                        }}
                        color="secondary"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </FormControl>
          </div>
          <Button
            variant="contained"
            disabled={isAnnouncementPostStarting}
            startIcon={<Add />}
            onClick={() => handle.startAnnouncementPost()}
          >
            ADD ANNOUNCEMENT POST
          </Button>
        </Grid>

        <Grid item sx={{ margin: "auto", width: "100%" }}>
          <DataGridTable
            data={rows || []}
            columns={masterViewColumns}
            onRowClick={(event) => {
              navigate(`/post/${event.id}`);
            }}
            rowsPerPageOptions={[10]}
            pageSize={10}
            rowCount={rowCountState}
            params={params}
            setParams={setParams}
            page={page}
            isLoading={isGettingAnnouncementPosts}
          />
        </Grid>
      </Grid>
    </React.Fragment>


  );
}


export default AnnouncementPostList